import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getAdminId } from '../../utils/getAdminId';
import { Clock, User, Package, Truck, FileText } from 'lucide-react';
import { format } from 'date-fns';

interface ActivityLog {
  id: string;
  user_id: string;
  user_name?: string;
  action_type: string;
  description: string;
  created_at: string;
  metadata?: any;
}

interface ActivityLoggerProps {
  maxItems?: number;
  showUserColumn?: boolean;
}

export const ActivityLogger: React.FC<ActivityLoggerProps> = ({
  maxItems = 10,
  showUserColumn = true
}) => {
  const { user } = useAuth();
  const [activities, setActivities] = useState<ActivityLog[]>([]);

  const adminId = getAdminId(user);

  useEffect(() => {
    if (!adminId) return;

    loadActivityHistory();

    const subscription = supabase
      .channel(`activity_logs_changes_${adminId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'activity_logs', filter: `admin_id=eq.${adminId}` },
        () => {
          loadActivityHistory();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [adminId]);

  const loadActivityHistory = async () => {
    try {
      if (!adminId) return;

      const { data, error } = await supabase
        .from('activity_logs')
        .select(`
          id,
          user_id,
          action_type,
          description,
          created_at,
          metadata,
          admin_id
        `)
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false })
        .limit(maxItems);

      if (error) {
        console.error('Failed to load activity history:', error);
        return;
      }

      const userIds = [...new Set((data || []).map((log: any) => log.user_id))];

      const { data: usersData } = await supabase
        .from('users')
        .select('id, full_name, email')
        .in('id', userIds);

      const usersMap = new Map(
        (usersData || []).map((u: any) => [u.id, u])
      );

      const formattedActivities = (data || []).map((activity: any) => {
        const activityUser = usersMap.get(activity.user_id);
        return {
          id: activity.id,
          user_id: activity.user_id,
          user_name: activityUser?.full_name || activityUser?.email?.split('@')[0] || 'Unknown User',
          action_type: activity.action_type,
          description: activity.description,
          created_at: activity.created_at,
          metadata: activity.metadata
        };
      });

      setActivities(formattedActivities);
    } catch (error) {
      console.error('Failed to load activity history:', error);
    }
  };

  const logActivity = async (action: string, details: string, data?: any) => {
    if (!user || !adminId) return;

    try {
      const { error } = await supabase
        .from('activity_logs')
        .insert({
          admin_id: adminId,
          user_id: user.id,
          action_type: action,
          description: details,
          metadata: data || {},
          entity_type: data?.entity_type || 'inventory',
          entity_id: data?.item_id || data?.entity_id || null
        });

      if (error) {
        console.error('Failed to save activity log:', error);
        return;
      }

      loadActivityHistory();
    } catch (error) {
      console.error('Failed to save activity log:', error);
    }
  };

  // Expose logging function globally
  React.useEffect(() => {
    (window as any).logActivity = logActivity;
    return () => {
      delete (window as any).logActivity;
    };
  }, [user, activities]);

  const getActionIcon = (action: string) => {
    const lowerAction = action.toLowerCase();
    if (lowerAction.includes('login') || lowerAction.includes('logout') || lowerAction.includes('user')) {
      return User;
    }
    if (lowerAction.includes('item') || lowerAction.includes('inventory') || lowerAction.includes('scan')) {
      return Package;
    }
    if (lowerAction.includes('shipment') || lowerAction.includes('ship')) {
      return Truck;
    }
    return FileText;
  };

  const getActionColor = (action: string) => {
    const lowerAction = action.toLowerCase();
    if (lowerAction.includes('login') || lowerAction.includes('create')) {
      return 'text-green-600 bg-green-100 dark:bg-green-900/30';
    }
    if (lowerAction.includes('logout') || lowerAction.includes('delete') || lowerAction.includes('remove')) {
      return 'text-red-600 bg-red-100 dark:bg-red-900/30';
    }
    if (lowerAction.includes('add') || lowerAction.includes('new')) {
      return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30';
    }
    if (lowerAction.includes('update') || lowerAction.includes('edit') || lowerAction.includes('modify')) {
      return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30';
    }
    if (lowerAction.includes('scan')) {
      return 'text-cyan-600 bg-cyan-100 dark:bg-cyan-900/30';
    }
    return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30';
  };

  const formatAction = (action: string) => {
    return action.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
          <Clock className="h-5 w-5 mr-2" />
          Recent Activity
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Last {Math.min(activities.length, maxItems)} actions
        </span>
      </div>

      {activities.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
          <Clock className="h-12 w-12 mb-4" />
          <p>No activity recorded yet</p>
        </div>
      ) : (
        <div className="flex-1 space-y-2 overflow-y-auto pr-1">
          {activities.slice(0, maxItems).map((activity) => {
            const ActionIcon = getActionIcon(activity.action_type);
            const activityDate = new Date(activity.created_at);

            return (
              <div key={activity.id} className="flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <div className={`p-2 rounded-full mr-3 flex-shrink-0 ${getActionColor(activity.action_type)}`}>
                  <ActionIcon className="h-4 w-4" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatAction(activity.action_type)}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                      {format(activityDate, 'MMM d, HH:mm')}
                    </span>
                  </div>

                  <div className="text-sm text-gray-600 dark:text-gray-400 truncate">
                    {activity.description}
                  </div>

                  {showUserColumn && activity.user_name && (
                    <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
                      <User className="h-3 w-3 mr-1" />
                      {activity.user_name}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Global activity logging function
export const logActivity = (action: string, details: string, data?: any) => {
  if (typeof window !== 'undefined' && (window as any).logActivity) {
    (window as any).logActivity(action, details, data);
  }
};